import { Alert } from "@repo/ui/components/ui/alert";
import { Button } from "@repo/ui/components/ui/button";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { ProfilFormData } from "@repo/ui/lib/api/types/profil";

interface ProfilFormFieldsProps {
  formData: ProfilFormData;
  error: string | null;
  droitOptions: any;
  isEditing: boolean;
  isLoadingDroits: boolean;
  isPending: boolean;
  onCancel: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleCheckboxChange: (name: string, checked: boolean) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

export function ProfilFormFields({
  formData,
  error,
  droitOptions,
  isEditing,
  isLoadingDroits,
  isPending,
  onCancel,
  handleInputChange,
  handleSelectChange,
  handleCheckboxChange,
  handleSubmit
}: ProfilFormFieldsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">
          {isEditing ? "Modifier un profil" : "Créer un nouveau profil"}
        </h2>
        <p className="text-gray-500 text-sm">
          {isEditing
            ? "Modifiez les informations du profil existant."
            : "Remplissez le formulaire pour créer un nouveau profil."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">{error}</Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nom du profil *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name || ""}
            onChange={handleInputChange}
            placeholder="Entrez le nom du profil"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="groupe_type">Type de groupe</Label>
          <Select
            value={formData.groupe_type || ""}
            onValueChange={(value) => handleSelectChange("groupe_type", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionnez un type de groupe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ADMIN">Admin</SelectItem>
              <SelectItem value="DENDROMAP">Dendromap</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="droit_id">Droit *</Label>
          {isLoadingDroits ? (
            <div className="text-sm text-gray-500">Chargement des droits...</div>
          ) : (
            <Select
              value={formData.droit_id?.toString() || ""}
              onValueChange={(value) => handleSelectChange("droit_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez un droit" />
              </SelectTrigger>
              <SelectContent>
                {droitOptions && droitOptions.data?.map((droit: any) => (
                  <SelectItem key={droit.id} value={droit.id.toString()}>
                    {droit.name || `Droit ${droit.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id="is_init"
            checked={formData.is_init || false}
            onCheckedChange={(checked) => handleCheckboxChange("is_init", checked as boolean)}
          />
          <Label htmlFor="is_init" className="cursor-pointer">Actif</Label>
        </div>

        {isEditing && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="deleted"
              checked={formData.deleted || false}
              onCheckedChange={(checked) => handleCheckboxChange("deleted", checked as boolean)}
            />
            <Label htmlFor="deleted" className="cursor-pointer">Marqué comme supprimé</Label>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={isPending}
          >
            {isPending ? "En cours..." : isEditing ? "Mettre à jour" : "Créer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
